import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { PRESET_COLORS } from '@bixi/core/internal';
import tinycolor from 'tinycolor2';
import { IAnyColor, IColorMode, IHsl, IHsv, IRgb } from './color-picker.type';
import { convertColor, mapColor } from './utils';

export const DEFAULT_COLOR = '#000000';

@Component({
  selector: 'bixi-color-panel',
  exportAs: 'bixiColorPanel',

  host: {
    '[class.bixi-color-panel]': 'true',
    '[class.bixi-color-panel-borderless]': 'borderless'
  },
  template: `
  <div class="bixi-color-panel-content">
    <bixi-color-saturation
      [hsl]="hsl"
      [hsv]="hsv"
      (colorChange)="onChange($event)"
      >
    </bixi-color-saturation>
    <div class="bixi-color-controls" [class.bixi-color-controls-hide-alpha]="hideAlpha">
      <div class="bixi-color-sliders">
        <bixi-color-hue
          [hsl]="hsl"
          (colorChange)="onChange($event)"
        >
        </bixi-color-hue>
        <bixi-color-alpha
          *ngIf="!hideAlpha"
          [hsl]="hsl"
          [rgb]="rgb"
          (colorChange)="onChange($event)"
          >
        </bixi-color-alpha>
      </div>
      <div class="bixi-color-preview">
        <bixi-color-block [color]="color"></bixi-color-block>
      </div>
    </div>
    <bixi-color-fields
      [hex]="hex"
      [rgb]="rgb"
      [hideAlpha]="hideAlpha"
      (colorChange)="onChange($event)">
    </bixi-color-fields>
  </div>
  <div class="bixi-color-panel-footer">
    <div
      class="bixi-color-presetcolor-item"
      *ngFor="let color of _presetColors">
      <div
        [style.border-color]="color.dark"
        [style.background-color]="color.light"
        (click)="onChange(color.dark)"
        [class.bixi-color-inactive]="!isActiveColor(color.dark)"
        class="bixi-color-presetcolor-wrap">
        <div
          class="bixi-color-presetcolor-inner"
          [style.background-color]="color.dark"
          [style.border-color]="color.dark">
        </div>
      </div>
    </div>
  </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BixiColorPanelComponent implements OnInit, OnChanges {
  hsl: IHsl;
  rgb: IRgb;
  hsv: IHsv;
  hex: string;
  _presetColors: {
    dark: string;
    light: string;
  }[] = [];

  @Input() color: IAnyColor;
  @Input() mode: IColorMode = 'hex';
  @Input() alphaDisabled = false;
  @Input() presetColors: string[] = PRESET_COLORS;
  @Input() borderless = false;
  @Output() colorChange = new EventEmitter<IAnyColor>();

  get hideAlpha() {
    return this.alphaDisabled || this.mode === 'hex';
  }

  ngOnInit() {
    this.initPresetColors();
  }

  isActiveColor(color: IAnyColor) {
    const { rgb } = convertColor(color);
    return rgb.a === this.rgb.a && rgb.b === this.rgb.b && rgb.g === this.rgb.g && rgb.r === this.rgb.r;
  }

  initPresetColors() {
    this._presetColors = (this.presetColors || []).map((c: IAnyColor) => {
      const colorInstance = tinycolor(c);
      const dark = colorInstance.toRgbString();
      colorInstance.setAlpha(0.3);
      const light = colorInstance.toRgbString();
      return {
        dark,
        light
      };
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const { color, presetColors } = changes;
    if (color) {
      const { hsl, hsv, hex, rgb } = convertColor(color.currentValue || DEFAULT_COLOR);
      this.hsl = hsl;
      this.hsv = hsv;
      this.hex = hex;
      this.rgb = rgb;
    }
    if (presetColors) {
      this.initPresetColors();
    }
  }

  onChange(anyColor: IAnyColor) {
    const color = mapColor(anyColor, this.mode);
    this.colorChange.emit(color);
  }
}
